import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { z } from "zod";

import Dialog, { useDialogContainer } from "@/src/components/dialog/Dialog";
import { MultiComboboxBase } from "@/src/components/form-fields/MultiCombobox";
import { TextField } from "@/src/components/form-fields/TextField";
import { Field, FieldLabel } from "@/src/components/ui/field";
import { useCountryGroupMutations } from "@/src/hooks/mutations/useCountryGroupMutations";
import { getCountryOptions } from "@/src/utils/data/Countries";

import type { JSX } from "react";

import type {
  CountryGroup,
  CountryGroupPayload,
} from "@/src/api/ProjectAPI.types";
import type { AppState } from "@/src/reducers";

const LABEL_PATTERN = /^[a-z0-9]+$/;

interface CountryGroupFormValues {
  label: string;
  countryUuidList: string[];
}

interface ApiValidationErrorData {
  errors?: { label?: string };
}

function getLabelErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as ApiValidationErrorData | undefined;
  return data?.errors?.label;
}

/**
 * Form body — rendered inside `Dialog` so it can read the dialog content
 * element via `useDialogContainer()` and portal the country combobox popover
 * inside the dialog.
 */
function CountryGroupFields(): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const container = useDialogContainer();
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const { control } = useFormContext<CountryGroupFormValues>();
  const { field: countryField } = useController({
    control,
    name: "countryUuidList",
  });

  const countryOptionList = useMemo(
    () => getCountryOptions(countries, false),
    [countries],
  );

  return (
    <React.Fragment>
      <TextField
        label={t("geographicGroups.groupName")}
        name="label"
        placeholder="europe27"
      />
      <Field>
        <FieldLabel>{t("geographicGroups.countries")}</FieldLabel>
        <MultiComboboxBase
          emptyLabel={t("editor-components:geoNoCountryFound")}
          onChange={countryField.onChange}
          options={countryOptionList}
          placeholder={t("editor-components:geoCountriesPlaceholder")}
          popoverContainer={container}
          searchPlaceholder={t("editor-components:geoSearchCountry")}
          value={countryField.value}
        />
      </Field>
    </React.Fragment>
  );
}

interface CountryGroupFormDialogProps {
  open: boolean;
  group: CountryGroup | null;
  onClose: () => void;
}

function CountryGroupFormDialog({
  open,
  group,
  onClose,
}: CountryGroupFormDialogProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const { createCountryGroup, updateCountryGroup, isSaving } =
    useCountryGroupMutations();

  const schema = useMemo(
    () =>
      z.object({
        label: z
          .string()
          .min(1, t("geographicGroups.groupNameRequired"))
          .regex(LABEL_PATTERN, t("geographicGroups.labelInvalid")),
        countryUuidList: z.array(z.string()),
      }),
    [t],
  );

  const methods = useForm<CountryGroupFormValues>({
    defaultValues: { label: "", countryUuidList: [] },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        label: group?.label ?? "",
        countryUuidList: group?.countries.map((country) => country.uuid) ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group]);

  const onValid = async (values: CountryGroupFormValues): Promise<void> => {
    const payload: CountryGroupPayload = {
      label: values.label,
      countries: values.countryUuidList.map((uuid) => ({ uuid })),
    };

    try {
      if (group) {
        await updateCountryGroup({ uuid: group.uuid, data: payload });
      } else {
        await createCountryGroup(payload);
      }
      onClose();
    } catch (error) {
      const labelError = getLabelErrorMessage(error);
      if (labelError) {
        methods.setError("label", {
          message: labelError.includes("taken")
            ? t("geographicGroups.labelTaken")
            : t("geographicGroups.labelInvalid"),
        });
        return;
      }
      toast.error(t("editor-notif:saveError"));
    }
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Dialog
        cancelLabel={t("editor-offers:cancel")}
        confirmLabel={t("editor-offers:save")}
        contentClassName="sm:max-w-md"
        description={t("geographicGroups.formSubtitle")}
        isConfirmDisabled={isSaving}
        isOpen={open}
        onConfirm={methods.handleSubmit(onValid)}
        onOpenChange={(next) => !next && onClose()}
        title={
          group
            ? t("geographicGroups.editGroup")
            : t("geographicGroups.newGroup")
        }
      >
        <CountryGroupFields />
      </Dialog>
    </FormProvider>
  );
}

export default CountryGroupFormDialog;
